/**
 * components/HeroSearch.jsx (v2.71.61 — live suggestions + keyboard nav)
 * ----------------------------------------------------------------------
 * Premium one-input search bar with live categorized suggestions.
 *
 * Suggestions stack (top → bottom inside the dropdown):
 *   1. Synthesized SUGGESTIONS — locations · BHK combos · property types
 *      (client-side from cached /api/locations + /api/filters)
 *   2. PROPERTY results — from /api/search (live, debounced 300ms)
 *
 * Keyboard navigation:
 *   ↓ / ↑   move highlight across the unified flat list
 *   Enter   activate the highlighted row (navigate or apply filter)
 *   Esc     close dropdown + blur input
 *
 * Backend untouched. Suggestion logic is in
 * `shared/hooks/useSearchSuggestions.js`.
 *
 * Touch behavior: rows have 44px+ tap targets; popover spans 100% of
 * the bar so mobile users can confidently tap any suggestion.
 */

import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api from "../lib/api";
import { scrollToResults } from "../lib/scrollToResults";
import { Search, Building2, MapPin, X, Home, BedDouble, Sparkles, Wand2 } from "lucide-react";
import { getPropertyThumbnail } from "../shared/utils/property";
import useSearchSuggestions from "../shared/hooks/useSearchSuggestions";
import { parseSearchQuery } from "../shared/utils/searchParser";

const fmtINR = (n) => {
  const num = Number(n) || 0;
  if (num >= 10000000) return `₹${(num / 10000000).toFixed(num % 10000000 === 0 ? 0 : 1)} Cr`;
  if (num >= 100000)   return `₹${(num / 100000).toFixed(num % 100000 === 0 ? 0 : 1)} L`;
  if (num >= 1000)     return `₹${(num / 1000).toFixed(0)}K`;
  return `₹${num}`;
};

const PLACEHOLDER = "Search by area, BHK, property type, metro, or budget…";

// In-memory lookup cache (30 min TTL) — shared across remounts.
const _cache = { locations: null, types: null, at: 0 };
const CACHE_TTL = 30 * 60 * 1000;

export default function HeroSearch({ testidPrefix = "hs" }) {
  const nav = useNavigate();
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [focused, setFocused] = useState(false);
  const [loading, setLoading] = useState(false);
  const [properties, setProperties] = useState([]);
  const [lookups, setLookups] = useState({ locations: [], types: [] });
  const [highlight, setHighlight] = useState(0);
  const containerRef = useRef(null);
  const inputRef = useRef(null);
  const panelRef = useRef(null);

  // ─── load lookup data once (cached for 30 min) ────────────────
  useEffect(() => {
    const now = Date.now();
    if (_cache.locations && now - _cache.at < CACHE_TTL) {
      setLookups({ locations: _cache.locations, types: _cache.types });
      return;
    }
    let cancelled = false;
    Promise.all([
      api.get("/locations").then((r) => r.data || []).catch(() => []),
      api.get("/filters").then((r) => r.data || []).catch(() => []),
    ]).then(([locs, filts]) => {
      const ptFilter = (filts || []).find((f) => f.key === "property_type");
      const types = (ptFilter?.options || []).filter(Boolean);
      _cache.locations = locs; _cache.types = types; _cache.at = now;
      if (!cancelled) setLookups({ locations: locs, types });
    });
    return () => { cancelled = true; };
  }, []);

  // ─── client-side synthesized suggestions ──────────────────────
  const suggestions = useSearchSuggestions(q, lookups);

  // ─── intelligent query parser (v2.71.62) ──────────────────────
  // Detects BHK + property type + budget + location in a free-text query.
  // Returns null when nothing structured was found.
  const parsed = useMemo(() => parseSearchQuery(q, lookups), [q, lookups]);

  // Build a "smart intent" suggestion row to surface at the top of the
  // dropdown when:
  //   • the parser detected ≥2 distinct dimensions, OR
  //   • it detected ≥1 dimension AND the original query has extra words
  //     (qualifiers like "furnished", "near metro") the parser stripped.
  // Otherwise the regular property/type suggestions already cover the case.
  const smartIntent = useMemo(() => {
    if (!parsed) return null;
    const wordCount = (parsed.original || "").split(/\s+/).filter(Boolean).length;
    const hasQualifierNoise = parsed.parts.length === 1 && wordCount >= 2;
    if (parsed.parts.length < 2 && !hasQualifierNoise) return null;
    return {
      id: "smart-intent",
      kind: "smart",
      label: parsed.label,
      sub: "Smart match",
      params: parsed.params,
    };
  }, [parsed]);

  // ─── 300ms debounced live property fetch ──────────────────────
  useEffect(() => {
    const clean = q.trim();
    if (!clean) { setProperties([]); setLoading(false); return; }
    setLoading(true);
    const ctl = new AbortController();
    const timer = setTimeout(async () => {
      try {
        const r = await api.get("/search", {
          params: { q: clean, limit: 12 },
          signal: ctl.signal,
        });
        setProperties((r.data?.results || []).filter((x) => x.kind === "property"));
      } catch (e) {
        if (e?.name !== "CanceledError" && e?.code !== "ERR_CANCELED") setProperties([]);
      } finally {
        setLoading(false);
      }
    }, 300);
    return () => { clearTimeout(timer); ctl.abort(); };
  }, [q]);

  // ─── flat list for keyboard navigation ────────────────────────
  // Order: smart intent (if any) → suggestions → property results.
  const flatList = useMemo(() => {
    const arr = [];
    if (smartIntent) arr.push({ ...smartIntent, _row: "smart" });
    suggestions.forEach((s) => arr.push({ ...s, _row: "suggestion" }));
    properties.forEach((p) => arr.push({ ...p, _row: "property", id: p.id, kind: "property" }));
    return arr;
  }, [smartIntent, suggestions, properties]);

  // Reset highlight when list changes
  useEffect(() => { setHighlight(0); }, [flatList.length, q]);

  // ─── outside click + ESC + arrow nav ──────────────────────────
  useEffect(() => {
    const onDoc = (e) => {
      if (open && containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    const onKey = (e) => {
      if (e.key === "Escape") {
        setOpen(false);
        inputRef.current?.blur();
        return;
      }
      if (!open || flatList.length === 0) return;
      if (e.key === "ArrowDown") {
        e.preventDefault();
        setHighlight((h) => Math.min(h + 1, flatList.length - 1));
      } else if (e.key === "ArrowUp") {
        e.preventDefault();
        setHighlight((h) => Math.max(h - 1, 0));
      } else if (e.key === "Enter") {
        e.preventDefault();
        const item = flatList[highlight];
        if (item) activateItem(item);
      }
    };
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onKey);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, flatList, highlight]);

  // Scroll the highlighted row into view (keyboard nav UX)
  useEffect(() => {
    if (!open || !panelRef.current) return;
    const row = panelRef.current.querySelector(`[data-row-idx="${highlight}"]`);
    if (row && row.scrollIntoView) {
      row.scrollIntoView({ block: "nearest" });
    }
  }, [highlight, open]);

  // ─── activation handlers ──────────────────────────────────────
  const activateItem = useCallback((item) => {
    setOpen(false);
    setQ("");
    if (item._row === "property" || item.kind === "property") {
      nav(`/property/${item.id}`);
      return;
    }
    // Suggestion or smart intent → navigate to filtered homepage
    const p = item.params || {};
    const qs = new URLSearchParams();
    if (p.main_area)     qs.set("main_area", p.main_area);
    if (p.sub_area)      qs.set("sub_area", p.sub_area);
    if (p.bhk)           qs.set("bhk", p.bhk);
    if (p.property_type) qs.set("property_type", p.property_type);
    if (p.min_price != null) qs.set("min_price", String(p.min_price));
    if (p.max_price != null) qs.set("max_price", String(p.max_price));
    nav(`/?${qs.toString()}`);
    // v2.71.72 — Use the global scroll-to-results helper. The 50 ms
    // legacy timeout below was duplicating the platform-wide convention
    // built in lib/scrollToResults.js.
    scrollToResults({ delay: 50 });
  }, [nav]);

  const showPanel = open && (q.trim().length > 0);

  return (
    <div
      ref={containerRef}
      className="relative w-full max-w-2xl mx-auto mt-8"
      data-testid={`${testidPrefix}-root`}
    >
      {/* ───────── Single premium search bar ───────── */}
      <div
        className={`group flex items-center bg-white border transition-all duration-300 ease-out
                    rounded-2xl h-14 sm:h-16 px-2
                    ${focused
                      ? "border-[#0055FF]/40 shadow-[0_12px_40px_-8px_rgba(0,85,255,0.18),0_4px_10px_-2px_rgba(15,23,42,0.08)] scale-[1.005]"
                      : "border-slate-200 shadow-[0_8px_32px_-8px_rgba(15,23,42,0.10),0_2px_6px_-2px_rgba(15,23,42,0.06)] hover:shadow-[0_12px_40px_-8px_rgba(15,23,42,0.16),0_4px_10px_-2px_rgba(15,23,42,0.10)]"}`}
      >
        <div className={`shrink-0 ml-2 sm:ml-3 w-9 h-9 sm:w-10 sm:h-10 flex items-center justify-center rounded-xl transition-colors duration-200
                         ${focused ? "bg-[#0055FF]/10 text-[#0055FF]" : "text-slate-500 group-hover:text-slate-700"}`}>
          <Search size={18} strokeWidth={2.2} />
        </div>
        <input
          ref={inputRef}
          type="text"
          value={q}
          onChange={(e) => { setQ(e.target.value); setOpen(true); }}
          onFocus={() => { setFocused(true); setOpen(true); }}
          onBlur={() => setFocused(false)}
          placeholder={PLACEHOLDER}
          aria-label="Search"
          aria-autocomplete="list"
          aria-expanded={showPanel}
          aria-controls={`${testidPrefix}-panel`}
          aria-activedescendant={showPanel && flatList[highlight] ? `${testidPrefix}-row-${highlight}` : undefined}
          role="combobox"
          data-testid={`${testidPrefix}-input`}
          className="flex-1 min-w-0 h-full bg-transparent border-0 outline-none px-2 sm:px-3 text-[14px] sm:text-[15.5px] text-slate-900 placeholder:text-slate-400 placeholder:font-normal font-medium"
        />
        {q && (
          <button
            type="button"
            onClick={() => { setQ(""); setProperties([]); inputRef.current?.focus(); }}
            aria-label="Clear search"
            data-testid={`${testidPrefix}-clear`}
            className="shrink-0 w-8 h-8 mr-1 sm:mr-2 flex items-center justify-center text-slate-400 hover:text-slate-700 rounded-full hover:bg-slate-100 transition-colors"
          >
            <X size={14} />
          </button>
        )}
      </div>

      {/* ───────── Live dropdown ───────── */}
      {showPanel && (
        <div
          id={`${testidPrefix}-panel`}
          ref={panelRef}
          data-testid={`${testidPrefix}-panel`}
          role="listbox"
          className="absolute top-full mt-2 left-0 right-0 z-50 bg-white rounded-2xl border border-slate-100
                     shadow-[0_16px_48px_-12px_rgba(15,23,42,0.18),0_4px_12px_-2px_rgba(15,23,42,0.08)]
                     overflow-hidden animate-in fade-in slide-in-from-top-2 duration-200
                     max-h-[min(70vh,560px)] overflow-y-auto"
        >
          {/* Smart intent block (top priority — premium gradient row) */}
          {smartIntent && (
            <button
              type="button"
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => activateItem({ ...smartIntent, _row: "smart" })}
              onMouseEnter={() => setHighlight(0)}
              data-row-idx={0}
              id={`${testidPrefix}-row-0`}
              role="option"
              aria-selected={highlight === 0}
              data-testid={`${testidPrefix}-smart-intent`}
              className={`w-full text-left flex items-center gap-3 px-3 py-3 transition-colors border-b border-slate-100
                          ${highlight === 0
                            ? "bg-gradient-to-r from-[#0055FF]/10 via-[#0055FF]/[0.06] to-transparent"
                            : "hover:bg-gradient-to-r hover:from-[#0055FF]/[0.06] hover:via-[#0055FF]/[0.03] hover:to-transparent active:bg-[#0055FF]/[0.08]"}`}
            >
              <div className="shrink-0 w-10 h-10 rounded-xl bg-gradient-to-br from-[#0055FF] to-[#0044CC] flex items-center justify-center text-white shadow-sm">
                <Wand2 size={15} />
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-[10px] uppercase tracking-[0.12em] font-semibold text-[#0055FF] mb-0.5">Smart match</div>
                <div className="text-[14px] font-bold text-slate-900 truncate">{smartIntent.label}</div>
              </div>
              <div className="shrink-0 text-[10px] uppercase tracking-wider font-semibold text-[#0055FF]">↵ Search</div>
            </button>
          )}

          {/* Suggestions block */}
          {suggestions.length > 0 && (
            <div data-testid={`${testidPrefix}-suggestions`}>
              <div className="px-3 pt-2.5 pb-1 text-[10px] uppercase tracking-[0.12em] font-semibold text-slate-400 flex items-center gap-1">
                <Sparkles size={10} /> Suggestions
              </div>
              {suggestions.map((s, idx) => {
                // Smart intent occupies index 0 when present — offset suggestions.
                const rowIdx = (smartIntent ? 1 : 0) + idx;
                return (
                  <SuggestionRow
                    key={s.id}
                    item={s}
                    idx={rowIdx}
                    highlight={highlight}
                    onClick={() => activateItem(s)}
                    onMouseEnter={() => setHighlight(rowIdx)}
                    testidPrefix={testidPrefix}
                  />
                );
              })}
            </div>
          )}

          {/* Property results */}
          {loading ? (
            <div className="p-5 space-y-2.5" data-testid={`${testidPrefix}-loading`}>
              {[0, 1, 2].map((i) => (
                <div key={i} className="flex items-center gap-3 animate-pulse">
                  <div className="w-11 h-11 rounded-xl bg-slate-100" />
                  <div className="flex-1 space-y-1.5">
                    <div className="h-3 w-3/4 bg-slate-100 rounded" />
                    <div className="h-2.5 w-1/2 bg-slate-50 rounded" />
                  </div>
                </div>
              ))}
            </div>
          ) : properties.length === 0 && suggestions.length === 0 && !smartIntent ? (
            <div className="px-4 py-8 text-center" data-testid={`${testidPrefix}-no-results`}>
              <Search size={22} className="mx-auto mb-2 text-slate-300" />
              <div className="text-[13px] font-semibold text-slate-700">No matches for "{q.trim()}"</div>
              <div className="text-[11.5px] text-slate-500 mt-1">Try an area, BHK, or property type</div>
            </div>
          ) : properties.length > 0 ? (
            <div data-testid={`${testidPrefix}-properties`}>
              <div className="px-3 pt-3 pb-1 text-[10px] uppercase tracking-[0.12em] font-semibold text-slate-400 border-t border-slate-100">
                Properties
              </div>
              {properties.map((p, j) => {
                const idx = (smartIntent ? 1 : 0) + suggestions.length + j;
                return (
                  <PropertyRow
                    key={p.id}
                    item={p}
                    idx={idx}
                    highlight={highlight}
                    onClick={() => activateItem({ ...p, _row: "property" })}
                    onMouseEnter={() => setHighlight(idx)}
                    testidPrefix={testidPrefix}
                  />
                );
              })}
            </div>
          ) : null}
        </div>
      )}
    </div>
  );
}

// ─── Rows ────────────────────────────────────────────────────────
function SuggestionRow({ item, idx, highlight, onClick, onMouseEnter, testidPrefix }) {
  const isHl = idx === highlight;
  const Icon = item.kind === "location" ? MapPin
    : item.kind === "bhk" ? BedDouble
    : item.kind === "type" ? Home
    : Sparkles;
  return (
    <button
      type="button"
      onMouseDown={(e) => e.preventDefault()} // keep input focus for keyboard
      onClick={onClick}
      onMouseEnter={onMouseEnter}
      data-row-idx={idx}
      id={`${testidPrefix}-row-${idx}`}
      role="option"
      aria-selected={isHl}
      data-testid={`${testidPrefix}-suggestion-${item.id}`}
      className={`w-full text-left flex items-center gap-3 px-3 py-2.5 transition-colors
                  ${isHl ? "bg-[#0055FF]/[0.06]" : "hover:bg-slate-50 active:bg-slate-100"}`}
    >
      <div className={`shrink-0 w-9 h-9 rounded-xl flex items-center justify-center transition-colors
                       ${isHl ? "bg-[#0055FF]/10 text-[#0055FF]" : "bg-slate-100 text-slate-500"}`}>
        <Icon size={14} />
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-[13.5px] font-semibold text-slate-900 truncate">{item.label}</div>
        <div className="text-[11px] text-slate-500 mt-0.5">{item.sub}</div>
      </div>
      <div className="shrink-0 text-[10px] uppercase tracking-wider font-semibold text-slate-400">
        ↵
      </div>
    </button>
  );
}

function PropertyRow({ item, idx, highlight, onClick, onMouseEnter, testidPrefix }) {
  const isHl = idx === highlight;
  const cover = getPropertyThumbnail(item);
  return (
    <button
      type="button"
      onMouseDown={(e) => e.preventDefault()}
      onClick={onClick}
      onMouseEnter={onMouseEnter}
      data-row-idx={idx}
      id={`${testidPrefix}-row-${idx}`}
      role="option"
      aria-selected={isHl}
      data-testid={`${testidPrefix}-result-${item.id}`}
      className={`w-full text-left flex items-center gap-3 px-3 py-2.5 transition-colors
                  ${isHl ? "bg-[#0055FF]/[0.06]" : "hover:bg-slate-50 active:bg-slate-100"}`}
    >
      <div className="shrink-0 w-11 h-11 rounded-xl bg-slate-100 overflow-hidden relative">
        <div className="absolute inset-0 flex items-center justify-center text-slate-300">
          <Building2 size={15} />
        </div>
        {cover && (
          <img
            src={cover}
            alt=""
            loading="lazy"
            onError={(e) => { e.currentTarget.style.display = "none"; }}
            className="absolute inset-0 w-full h-full object-cover"
          />
        )}
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-[13.5px] font-semibold text-slate-900 truncate">{item.title}</div>
        <div className="text-[11.5px] text-slate-500 flex items-center gap-1 mt-0.5 truncate">
          {item.main_area && <><MapPin size={9} className="shrink-0" /><span className="truncate">{item.main_area}{item.sub_area ? ` · ${item.sub_area}` : ""}</span></>}
          {item.bhk && <span className="ml-1">· {item.bhk}</span>}
          {item.property_type && <span className="ml-1">· {item.property_type}</span>}
        </div>
      </div>
      <div className="shrink-0 text-right">
        <div className="text-[12.5px] font-semibold text-indigo-700 font-mono-data">
          {fmtINR(item.price)}
        </div>
      </div>
    </button>
  );
}
